import { AdConsentAnswer } from '@cf-shared/ad-consent';

/**
 * Whether the subscribe page should rebuild its checkout because this browser's
 * Advertising answer changed (#5587). Kept pure so the rules can be tested: a
 * mistake here is silent — the checkout simply carries the older answer and an EU
 * "yes" is never reported, or a checkout the buyer is typing into is reset.
 */

export enum AdConsentRefreshDecision {
  /** leave the checkout alone */
  Skip = 'skip',
  /** a price load is running and may have used the old answer: look again after it */
  RetryAfterLoad = 'retry-after-load',
  /** rebuild the checkout now */
  Refresh = 'refresh',
}

export interface AdConsentRefreshState {
  /** the native apps sell through the app stores, which are never uploaded */
  isNativePlatform: boolean;
  /** the page is gone or leaving */
  pageGone: boolean;
  loadingPrices: boolean;
  /** the buyer has already paid on this page */
  purchaseComplete: boolean;
  /** the page has a checkout to rebuild at all */
  hasPlans: boolean;
  /** the answer the current checkout was built with */
  builtWith: AdConsentAnswer | undefined;
  /** the answer this browser holds now */
  current: AdConsentAnswer | undefined;
}

export function decideAdConsentRefresh(state: AdConsentRefreshState): AdConsentRefreshDecision {
  if (state.isNativePlatform || state.pageGone) return AdConsentRefreshDecision.Skip;
  if (state.loadingPrices) return AdConsentRefreshDecision.RetryAfterLoad;
  if (state.purchaseComplete || !state.hasPlans) return AdConsentRefreshDecision.Skip;
  // the same answer is already built in: a sign-in that merely copies it onto this
  // browser must not reset a checkout the buyer is typing into
  if (state.current === state.builtWith) return AdConsentRefreshDecision.Skip;
  return AdConsentRefreshDecision.Refresh;
}
